import { cva, cx, type VariantProps } from '@/cva.config';

const ringVariants = cva({
  base: 'relative shrink-0',
  defaultVariants: { size: 'md' },
  variants: {
    size: {
      lg: 'size-14',
      md: 'size-10',
      sm: 'size-7',
    },
  },
});

const ringLabelVariants = cva({
  base: 'absolute inset-0 flex items-center justify-center font-mono font-semibold tabular-nums',
  variants: {
    size: {
      lg: 'text-sm lg:text-base',
      md: 'text-xs lg:text-sm',
      sm: 'text-2xs lg:text-xs',
    },
    tier: {
      high: 'text-score-high',
      low: 'text-score-low',
      mid: 'text-score-mid',
      none: 'text-foreground-muted',
    },
  },
});

interface ScoreRingProps extends VariantProps<typeof ringLabelVariants> {
  className?: string;
  score: number | null;
}

export const ScoreRing = ({
  className,
  score,
  size = 'md',
  tier = 'none',
}: ScoreRingProps) => {
  const dimension = {
    lg: 56,
    md: 40,
    sm: 28,
  }[size];
  const radius = (dimension - 4) / 2;
  const circumference = 2 * Math.PI * radius;
  const progress = score == null ? 0 : score / 100;
  const dashOffset = circumference * (1 - progress);

  return (
    <div className={cx(ringVariants({ size }), className)}>
      <svg className="-rotate-90" height={dimension} width={dimension}>
        <title>Score ring</title>
        <circle
          cx={dimension / 2}
          cy={dimension / 2}
          fill="none"
          r={radius}
          stroke="var(--color-border)"
          strokeWidth="2"
        />
        <circle
          cx={dimension / 2}
          cy={dimension / 2}
          fill="none"
          r={radius}
          stroke={
            {
              high: 'var(--color-score-high)',
              low: 'var(--color-score-low)',
              mid: 'var(--color-score-mid)',
              none: 'var(--color-border-active)',
            }[tier]
          }
          strokeDasharray={circumference}
          strokeDashoffset={dashOffset}
          strokeLinecap="round"
          strokeWidth="2.5"
          style={{ transition: 'stroke-dashoffset 0.4s ease' }}
        />
      </svg>
      <div className={cx(ringLabelVariants({ size, tier }))}>
        {score ?? '–'}
      </div>
    </div>
  );
};
